import React, { useState, useEffect, useMemo } from 'react';
import { usePisos } from '../hooks/usePisos';
import { usePagination } from '../hooks/usePagination';
import { Piso, CreatePisoForm, UpdatePisoForm } from '../types';

interface FormErrors {
  nombre?: string;
  numeroPiso?: string;
  descripcion?: string;
  general?: string;
}

const Pisos: React.FC = () => {
  const { 
    pisos, 
    pisosActivos,
    isLoading, 
    isLoadingActivos,
    error,
    createPiso, 
    updatePiso, 
    deletePiso, 
    isCreating, 
    isUpdating, 
    isDeleting 
  } = usePisos();
  
  const [showForm, setShowForm] = useState(false);
  const [editingPiso, setEditingPiso] = useState<Piso | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivos, setFilterActivos] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState<CreatePisoForm>({
    nombre: '',
    descripcion: '',
    numeroPiso: 1,
  });

  // Limpiar mensajes de éxito después de 3 segundos
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      errors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    if (formData.descripcion && formData.descripcion.length > 500) {
      errors.descripcion = 'La descripción no puede exceder 500 caracteres';
    }

    // Verificar duplicados
    const pisosToCheck = filterActivos ? pisosActivos : pisos;
    const existingPiso = pisosToCheck.find(p => 
      p.nombre.toLowerCase() === formData.nombre.toLowerCase() && 
      (!editingPiso || p.id !== editingPiso.id)
    );
    if (existingPiso) {
      errors.nombre = 'Ya existe un piso con este nombre';
    }

    const existingNumero = pisosToCheck.find(p => 
      p.numeroPiso === formData.numeroPiso && 
      (!editingPiso || p.id !== editingPiso.id)
    );
    if (existingNumero) {
      errors.numeroPiso = 'Ya existe un piso con este número';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtrar pisos según búsqueda y filtro de estado (sobre el total, luego se pagina)
  const filteredPisos = useMemo(() => {
    const base = filterActivos ? pisosActivos : pisos;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return base;
    return base.filter(piso =>
      piso.nombre.toLowerCase().includes(term) ||
      (piso.descripcion?.toLowerCase().includes(term) ?? false) ||
      piso.numeroPiso.toString().includes(term)
    );
  }, [pisos, pisosActivos, filterActivos, searchTerm]);

  const { page, setPage, totalItems, totalPages, pageStart, pageEnd, pageItems, goPrevPage, goNextPage } = usePagination(filteredPisos);

  // Reset de página al cambiar búsqueda o filtro
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterActivos, setPage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (editingPiso) {
      const updateData: UpdatePisoForm = {
        ...formData,
        activo: editingPiso.activo
      };
      updatePiso({ id: editingPiso.id, piso: updateData }, {
        onSuccess: () => {
          setSuccessMessage('Piso actualizado exitosamente');
          setShowSuccessMessage(true);
          handleCancel();
        },
        onError: (error: any) => {
          setFormErrors({ general: error.message || 'Error al actualizar el piso' });
        }
      });
    } else {
      createPiso(formData, {
        onSuccess: () => {
          setSuccessMessage('Piso creado exitosamente');
          setShowSuccessMessage(true);
          handleCancel();
        },
        onError: (error: any) => {
          setFormErrors({ general: error.message || 'Error al crear el piso' });
        }
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPiso(null);
    setFormErrors({});
    setFormData({
      nombre: '',
      descripcion: '',
      numeroPiso: 1,
    });
  };

  const handleEdit = (piso: Piso) => {
    setEditingPiso(piso);
    setFormData({
      nombre: piso.nombre,
      descripcion: piso.descripcion || '',
      numeroPiso: piso.numeroPiso,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este piso? Esta acción no se puede deshacer.')) {
      try {
        await deletePiso(id);
        setSuccessMessage('Piso eliminado exitosamente');
        setShowSuccessMessage(true);
      } catch (error) {
      }
    }
  };

  const handleToggleActivo = async (piso: Piso) => {
    try {
      const updateData: UpdatePisoForm = {
        nombre: piso.nombre,
        descripcion: piso.descripcion || '',
        numeroPiso: piso.numeroPiso,
        activo: !piso.activo
      };
      await updatePiso({ id: piso.id, piso: updateData });
      setSuccessMessage(`Piso ${!piso.activo ? 'activado' : 'desactivado'} exitosamente`);
      setShowSuccessMessage(true);
    } catch (error) {
    }
  };

  const handleInputChange = (field: keyof CreatePisoForm, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Limpiar error general cuando el usuario empiece a escribir
    if (formErrors.general) {
      setFormErrors(prev => ({ ...prev, general: undefined }));
    }
  };

  if (isLoading || isLoadingActivos) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando pisos...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error al cargar pisos</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error.message || 'Ha ocurrido un error inesperado. Por favor, intenta de nuevo.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 lg:px-8">
      <div className="py-6">
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900">Gestión de Pisos</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Administra los pisos de la clínica</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Nuevo Piso
          </button>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Buscar por nombre, descripción o número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Filtro de Estado
              </label>
              <select
                value={filterActivos ? 'activos' : 'todos'}
                onChange={(e) => setFilterActivos(e.target.value === 'activos')}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todos los pisos</option>
                <option value="activos">Solo activos</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs sm:text-sm text-gray-600">
                {totalItems} piso{totalItems !== 1 ? 's' : ''} encontrado{totalItems !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div className="bg-white shadow rounded-lg p-3 sm:p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingPiso ? 'Editar Piso' : 'Nuevo Piso'}
            </h2>
            <form onSubmit={handleSubmit}>
              {/* Error general */}
              {formErrors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <svg className="flex-shrink-0 h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{formErrors.general}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.nombre ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.nombre}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Número de Piso *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.numeroPiso}
                    onChange={(e) => handleInputChange('numeroPiso', parseInt(e.target.value) || 0)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.numeroPiso ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.numeroPiso && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.numeroPiso}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <input
                    type="text"
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.descripcion ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.descripcion}</p>
                  )}
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="order-2 sm:order-1 bg-gray-300 text-gray-700 px-4 py-3 w-full sm:w-auto rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreating || isUpdating}
                  className="order-1 sm:order-2 bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  {isCreating || isUpdating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </div>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {totalItems === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm ? 'No se encontraron pisos que coincidan con la búsqueda.' : 'No hay pisos disponibles.'}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {pageItems.map((piso) => (
                <li key={piso.id}>
                  <div className="px-3 py-3 sm:px-4 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                          piso.activo ? 'bg-purple-500' : 'bg-gray-400'
                        }`}>
                          <span className="text-white text-sm font-medium">
                            {piso.nombre.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <div className="text-sm sm:text-base font-medium text-gray-900">
                          {piso.nombre} - Piso {piso.numeroPiso}
                        </div>
                        {piso.descripcion && (
                          <div className="text-xs sm:text-sm text-gray-500">
                            {piso.descripcion}
                          </div>
                        )}
                        <div className="text-xs sm:text-sm text-gray-400">
                          Estado: {piso.activo ? 'Activo' : 'Inactivo'}
                          {piso.areas && piso.areas.length > 0 && (
                            <span className="ml-2">
                              • {piso.areas.length} área{piso.areas.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => handleToggleActivo(piso)}
                        disabled={isUpdating}
                        className={`text-sm font-medium px-3 py-3 w-full sm:w-auto text-center rounded transition-colors ${
                          piso.activo 
                            ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                            : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                        } disabled:opacity-50`}
                      >
                        {piso.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        onClick={() => handleEdit(piso)}
                        className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 text-sm font-medium px-3 py-3 w-full sm:w-auto text-center rounded transition-colors"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(piso.id)}
                        disabled={isDeleting}
                        className="text-red-600 hover:text-red-900 hover:bg-red-50 text-sm font-medium px-3 py-3 w-full sm:w-auto text-center rounded transition-colors disabled:opacity-50"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Paginación */}
        {totalItems > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-600">
              Mostrando {pageStart + 1}–{pageEnd} de {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goPrevPage}
                disabled={page === 0}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página anterior"
              >
                ← Anterior
              </button>
              <span className="text-xs sm:text-sm text-gray-600">
                Página {page + 1} de {totalPages}
              </span>
              <button
                onClick={goNextPage}
                disabled={page >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página siguiente"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pisos; 
